import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Target, CheckCircle2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';

// Lightweight daily-quest widget. Polls every 2 min while visible.
export default function DailyQuest() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const [data, setData] = useState(null);

  const load = () => api.get('/stickers/quest/today').then(r => setData(r.data)).catch(() => setData(null));

  useEffect(() => {
    if (!user || !['admin', 'manager', 'agent'].includes(user.role)) return;
    load();
    const id = setInterval(load, 120_000);
    const onGrant = () => load();
    window.addEventListener('sticker:granted', onGrant);
    return () => { clearInterval(id); window.removeEventListener('sticker:granted', onGrant); };
  }, [user]);

  if (!user || !['admin', 'manager', 'agent'].includes(user.role)) return null;
  if (!data || !data.quest) return null;

  const name = i18n.language === 'en' ? data.quest.name_en : data.quest.name_es;
  const pct = Math.min(100, Math.round((data.progress / data.target) * 100));
  const done = data.completed;

  return (
    <div className="card" style={{
      height: '100%',
      borderColor: done ? '#10b981' : 'var(--c-border)',
      background: done ? 'rgba(16, 185, 129, 0.08)' : 'var(--c-surface)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{
          width: 38, height: 38, borderRadius: 8,
          background: done ? '#10b981' : 'var(--c-surface-2)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: done ? '#fff' : 'var(--c-text-muted)',
          flexShrink: 0,
        }}>
          {done ? <CheckCircle2 size={20} /> : <Target size={20} />}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--c-text-muted)', textTransform: 'uppercase', letterSpacing: 0.5 }}>
            🎯 {t('dashboard.dailyQuest.title')}
          </div>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--c-text)', marginTop: 2 }}>
            {name}
          </div>
          <div style={{
            position: 'relative', marginTop: 6,
            height: 6, borderRadius: 3,
            background: 'var(--c-surface-2)',
            overflow: 'hidden',
          }}>
            <div style={{
              position: 'absolute', left: 0, top: 0, bottom: 0,
              width: `${pct}%`,
              background: done ? '#10b981' : 'var(--c-brand, #3b82f6)',
              transition: 'width 600ms ease',
            }} />
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2, flexShrink: 0 }}>
          <div style={{
            fontSize: 14, fontWeight: 700, color: done ? '#10b981' : 'var(--c-text)',
            fontVariantNumeric: 'tabular-nums',
          }}>
            {data.progress} / {data.target}
          </div>
          {data.streak > 0 && (
            <div title={t('dashboard.dailyQuest.streakTooltip', { count: data.streak })}
                 style={{
                   fontSize: 11, fontWeight: 600,
                   color: data.streak >= 7 ? '#f59e0b' : 'var(--c-text-muted)',
                   display: 'flex', alignItems: 'center', gap: 2,
                 }}>
              🔥 {data.streak}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
